import { useId, useState } from 'react';
import clsx from 'clsx';

type PasswordInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * A password field with a show/hide toggle.
 *
 * The toggle is a real `<button>` rather than a decorated icon so it is
 * keyboard reachable, and it never mutates `value` — flipping the input `type`
 * is all it does, so a half-typed password survives the reveal. Revealed state
 * is per-field and resets on unmount; nothing about it is persisted.
 */
export function PasswordInput({ className, ...props }: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false);
  const hintId = useId();

  return (
    <div className="relative">
      <input
        {...props}
        type={revealed ? 'text' : 'password'}
        // Room for the toggle, so a long password never runs under the icon.
        className={clsx('input pr-11', className)}
      />
      <button
        // `button`, not submit — revealing a password must never submit the form.
        type="button"
        onClick={() => setRevealed((on) => !on)}
        aria-pressed={revealed}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-describedby={hintId}
        title={revealed ? 'Hide password' : 'Show password'}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-control p-2 text-muted
                   hover:text-ink focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {revealed ? <EyeOffIcon /> : <EyeIcon />}
      </button>
      <span id={hintId} className="sr-only" role="status">
        {revealed ? 'Password is visible.' : 'Password is hidden.'}
      </span>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.6 6.2A9.9 9.9 0 0 1 12 6c6.4 0 10 6 10 6a17.6 17.6 0 0 1-3.2 3.9" />
      <path d="M6.6 6.7A17.4 17.4 0 0 0 2 12s3.6 6 10 6a9.7 9.7 0 0 0 4.2-.9" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m3 3 18 18" />
    </svg>
  );
}
